import React from 'react';
import styled from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, LabelUppercase } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import { useGetAssessmentTests } from '../../../hooks/use-hydra/admin';
import Button from '../../elements/Button';
import { toast } from 'react-toastify';
import { addAssessmentTestCohort } from '../../../api/admin';
import { useMutation } from 'react-query';
import useAuth from '../../../hooks/use-auth';

import { Input } from '../../elements/Input';
const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 80vw;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;

export default function CreateAssessmentTestCohortModal({
  isOpen,
  onClose,
  companyId,
}: {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
}) {
  const {
    refetch: refetchAssessmentTests,
    isLoading: isLoadingAssessmentTests,
  } = useGetAssessmentTests(companyId);

  const { getTokenSilently } = useAuth();
  const [cohortName, setCohortName] = React.useState('');

  const addAssessmentTestCohortMutation = useMutation({
    mutationFn: async (name: string) =>
      await addAssessmentTestCohort(await getTokenSilently(), {
        companyId,
        name,
      }),
    onSuccess: async () => {
      await refetchAssessmentTests();
      toast.success('Assessment test cohort created successfully');

      onClose();
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : 'Failed to create cohort'
      );
    },
  });

  React.useEffect(() => {
    if (!isOpen) {
      setCohortName('');
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <HeaderRow>
        <Header>Create Assessment Test Cohort</Header>
        <CloseButton onClick={onClose} />
      </HeaderRow>
      <Content>
        <div
          style={{
            width: 'min-content',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            alignItems: 'flex-start',
          }}
        >
          <LabelUppercase>Cohort Name</LabelUppercase>
          <Input
            style={{ minWidth: 400, marginTop: 0 }}
            value={cohortName}
            onChange={(e) => setCohortName(e.target.value)}
            placeholder="Enter the name of the cohort"
          />
          <Button
            label="Create Cohort"
            type="primary"
            showIconBeforeLabel
            faIcon="fa-solid fa-plus"
            onClick={() => {
              addAssessmentTestCohortMutation.mutate(cohortName.trim());
            }}
            isLoading={
              addAssessmentTestCohortMutation.isLoading ||
              isLoadingAssessmentTests
            }
          />
        </div>
      </Content>
    </Wrapper>
  );
}
